import React, { useState, useEffect, useRef } from 'react'
import { Button, Tooltip } from '@mui/material'
import { Search, Building2, CreditCard, ArrowRight, Zap, ChevronDown, Globe } from 'lucide-react'
import ReactCountryFlag from 'react-country-flag'
import api from "../../../../api/axios"
import { SUPPORTED_COUNTRIES, Country } from '../countries'

type Props = {
    onClose: () => void
}

export const StripeOnboardingWithdrawModal: React.FC<Props> = ({ onClose }) => {
    const [dropdownSearch, setDropdownSearch] = useState('')
    const [overviewSearch, setOverviewSearch] = useState('')
    const [loading, setLoading] = useState(false)
    const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
    const [detectingLocation, setDetectingLocation] = useState<boolean>(true)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Fetch user country via IP on component mount to set default selection
    useEffect(() => {
        const fetchUserCountry = async () => {
            try {
                const response = await fetch('https://ipapi.co/json/')
                const data = await response.json()
                if (data && data.country_code) {
                    const code = data.country_code.toLowerCase()
                    const found = SUPPORTED_COUNTRIES.find(c => c.code === code)
                    if (found) {
                        setSelectedCountry(found)
                    } else {
                        setSelectedCountry(SUPPORTED_COUNTRIES.find(c => c.code === 'de') || SUPPORTED_COUNTRIES[0])
                    }
                } else {
                    setSelectedCountry(SUPPORTED_COUNTRIES.find(c => c.code === 'de') || SUPPORTED_COUNTRIES[0])
                }
            } catch (error) {
                console.error('Failed to fetch location by IP', error)
                setSelectedCountry(SUPPORTED_COUNTRIES.find(c => c.code === 'de') || SUPPORTED_COUNTRIES[0])
            } finally {
                setDetectingLocation(false)
            }
        }

        fetchUserCountry()
    }, [])

    // Close custom dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Handle onboarding request submission with selected country code
    const handleOnboardClick = async () => {
        if (!selectedCountry) return
        try {
            setLoading(true)
            const countryCode = selectedCountry.code.toUpperCase()
            const response = await api.post<{ onboardingUrl: string }>(`/stripe/connect/onboard?countryCode=${countryCode}`)
            if (response.data.onboardingUrl) {
                window.location.href = response.data.onboardingUrl
            }
        } catch (error) {
            console.error('Failed to initialize Stripe Connect onboarding', error)
            alert('Could not start onboarding. Please try again later.')
        } finally {
            setLoading(false)
        }
    }

    const dropdownFilteredCountries = SUPPORTED_COUNTRIES.filter(country =>
        country.name.toLowerCase().includes(dropdownSearch.toLowerCase())
    )

    const overviewFilteredCountries = SUPPORTED_COUNTRIES.filter(country =>
        country.name.toLowerCase().includes(overviewSearch.toLowerCase())
    )

    return (
        <div className="flex flex-col gap-4 p-4 text-light-text dark:text-dark-text bg-white dark:bg-neutral-900/40 max-h-[85vh] overflow-y-auto">
            <div className="flex flex-col gap-1.5 p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700/60 shadow-md">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-light-primary dark:text-dark-primary">
                    <Zap size={14} /> Stripe Connect Payouts
                </div>
                <h2 className="text-xl font-black tracking-tight text-light-text dark:text-white">
                    Complete Stripe Onboarding
                </h2>
                <p className="text-xs text-gray-600 dark:text-neutral-400">
                    Connect your account securely to enable direct bank transfers and global payout options.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Step 1: Select Country */}
                <div className="flex flex-col gap-3 p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700/60 shadow-md">
                    <div className="flex items-center gap-3">
                        <span className="w-5 h-5 rounded-full bg-blue-500/10 text-blue-500 flex items-center justify-center text-xs font-bold">1</span>
                        <h3 className="text-sm font-black tracking-tight text-light-text dark:text-white">Select Your Country</h3>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-neutral-400">
                        Choose the country where your legal entity or bank account is registered.
                    </p>

                    <div className="relative" ref={dropdownRef}>
                        <button
                            type="button"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="w-full flex items-center justify-between gap-3 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded-lg px-3.5 py-2.5 text-xs font-bold text-light-text dark:text-white focus:outline-none focus:border-blue-500 shadow-sm"
                        >
                            <div className="flex items-center gap-2.5 truncate">
                                {detectingLocation ? (
                                    <span className="text-gray-400 text-xs font-normal">Detecting location...</span>
                                ) : selectedCountry ? (
                                    <>
                                        <ReactCountryFlag svg countryCode={selectedCountry.code} style={{ width: '1.4em', height: '1.4em', flexShrink: 0 }} />
                                        <span className="truncate">{selectedCountry.name}</span>
                                    </>
                                ) : (
                                    <span className="text-gray-400 text-xs font-normal">Select country...</span>
                                )}
                            </div>
                            <ChevronDown size={14} className={`text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isDropdownOpen && (
                            <div className="absolute left-0 right-0 mt-1.5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-xl z-50 overflow-hidden flex flex-col">
                                <div className="p-2.5 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
                                    <div className="relative">
                                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search country..."
                                            value={dropdownSearch}
                                            onChange={(e) => setDropdownSearch(e.target.value)}
                                            className="w-full bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded-md pl-8 pr-3 py-2 text-xs text-light-text dark:text-white focus:outline-none focus:border-blue-500"
                                            autoFocus
                                        />
                                    </div>
                                </div>
                                <div className="max-h-72 overflow-y-auto p-1.5 flex flex-col gap-1">
                                    {dropdownFilteredCountries.length > 0 ? (
                                        dropdownFilteredCountries.map(country => (
                                            <div
                                                key={country.code}
                                                onClick={() => {
                                                    setSelectedCountry(country)
                                                    setIsDropdownOpen(false)
                                                    setDropdownSearch('')
                                                }}
                                                className={`flex items-center justify-between px-3.5 py-2 rounded-md text-xs cursor-pointer transition-colors ${selectedCountry?.code === country.code ? 'bg-blue-500/10 text-blue-500 font-bold' : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-light-text dark:text-neutral-200'}`}
                                            >
                                                <div className="flex items-center gap-2.5 truncate">
                                                    <ReactCountryFlag svg countryCode={country.code} style={{ width: '1.2em', height: '1.2em', flexShrink: 0 }} />
                                                    <span className="truncate">{country.name}</span>
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0 text-gray-400">
                                                    {country.supportsBank && (
                                                        <Tooltip title="Bank Transfer" arrow>
                                                            <span><Building2 size={12} className="text-emerald-500" /></span>
                                                        </Tooltip>
                                                    )}
                                                    {country.supportsCrypto && (
                                                        <Tooltip title="Crypto Payout" arrow>
                                                            <span className="w-3 h-3 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 text-[8px] font-black">$</span>
                                                        </Tooltip>
                                                    )}
                                                    {country.supportsDebit && (
                                                        <Tooltip title="Debit Card" arrow>
                                                            <span><CreditCard size={12} className="text-blue-500" /></span>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-3 text-center text-xs text-gray-500">No country found</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {selectedCountry && (
                        <div className="bg-white dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-700/60 rounded-md p-2.5 flex flex-col gap-1.5">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                Supported Payout Methods:
                            </div>
                            <div className="flex items-center gap-2 flex-wrap text-xs text-light-text dark:text-neutral-200">
                                {selectedCountry.supportsBank && (
                                    <span className="flex items-center gap-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2 py-1 rounded border border-emerald-500/20">
                                        <Building2 size={12} /> Direct Bank Transfer
                                    </span>
                                )}
                                {selectedCountry.supportsCrypto && (
                                    <span className="flex items-center gap-1.5 bg-orange-500/10 text-orange-600 dark:text-orange-400 px-2 py-1 rounded border border-orange-500/20">
                                        <span className="w-3 h-3 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 text-[8px] font-black">$</span> Crypto Payouts
                                    </span>
                                )}
                                {selectedCountry.supportsDebit && (
                                    <span className="flex items-center gap-1.5 bg-blue-500/10 text-blue-600 dark:text-blue-400 px-2 py-1 rounded border border-blue-500/20">
                                        <CreditCard size={12} /> Debit Card
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Step 2: Connect Button */}
                <div className="flex flex-col justify-between p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700/60 shadow-md">
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                            <span className="w-5 h-5 rounded-full bg-blue-500/10 text-blue-500 flex items-center justify-center text-xs font-bold">2</span>
                            <h3 className="text-sm font-black tracking-tight text-light-text dark:text-white">Connect to Stripe</h3>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-neutral-400">
                            {selectedCountry
                                ? `Ready to proceed with ${selectedCountry.name}. Click below to open the secure Stripe onboarding workflow.`
                                : 'Please select a country in Step 1 to enable the connection.'}
                        </p>
                    </div>

                    <div className="mt-3">
                        <div className="relative group w-full">
                            {selectedCountry && (
                                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-indigo-500 to-blue-700 rounded-lg blur-md opacity-85 group-hover:opacity-100 transition duration-300 animate-pulse" />
                            )}
                            <Button
                                variant="contained"
                                disabled={loading || !selectedCountry}
                                onClick={handleOnboardClick}
                                className={`!relative !w-full !font-semibold !px-4 !py-2.5 !rounded-lg !shadow-lg !normal-case !flex !items-center !justify-center !gap-2 ${selectedCountry ? '!bg-dark-primary !text-white' : '!bg-neutral-300 dark:!bg-neutral-700 !text-neutral-500 dark:!text-neutral-400 !cursor-not-allowed'}`}
                            >
                                {loading ? 'Preparing...' : 'Start Stripe Onboarding'}
                                <ArrowRight size={14} />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Complete Country Overview Section */}
            <div className="p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700/60 shadow-md">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-3 gap-3">
                    <div>
                        <h3 className="text-sm font-black tracking-tight flex items-center gap-2 text-light-text dark:text-white">
                            <Globe size={15} className="text-light-primary dark:text-dark-primary" /> Complete Country Overview
                        </h3>
                        <p className="text-[11px] text-gray-600 dark:text-neutral-400">Browse all supported countries and available payout methods.</p>
                    </div>

                    <div className="relative w-full sm:w-52">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-400" />
                        <input
                            type="text"
                            placeholder="Filter countries..."
                            value={overviewSearch}
                            onChange={e => setOverviewSearch(e.target.value)}
                            className="w-full bg-white dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-700 rounded-md pl-8 pr-3 py-1.5 text-xs text-light-text dark:text-dark-text focus:outline-none focus:border-blue-500"
                        />
                    </div>
                </div>

                <div className="max-h-[260px] overflow-y-auto pr-1.5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {overviewFilteredCountries.map(country => (
                        <div
                            key={country.code}
                            onClick={() => {
                                setSelectedCountry(country)
                                window.scrollTo({ top: 0, behavior: 'smooth' })
                            }}
                            className={`bg-white dark:bg-neutral-900/60 border rounded-md p-2 flex items-center justify-between shadow-sm cursor-pointer transition-colors ${selectedCountry?.code === country.code ? 'border-blue-500 bg-blue-500/5' : 'border-neutral-200 dark:border-neutral-700/60 hover:border-blue-500'}`}
                        >
                            <div className="flex items-center gap-2.5 min-w-0">
                                <ReactCountryFlag svg countryCode={country.code} style={{ width: '1.2em', height: '1.2em', flexShrink: 0 }} />
                                <span className="text-xs font-bold truncate text-light-text dark:text-neutral-200">{country.name}</span>
                            </div>
                            <div className="flex items-center gap-1 shrink-0 text-gray-400 dark:text-neutral-400">
                                {country.supportsBank && (
                                    <Tooltip title="Supports direct bank transfer" arrow>
                                        <span className="cursor-help flex items-center">
                                            <Building2 size={12} className="text-emerald-500" />
                                        </span>
                                    </Tooltip>
                                )}
                                {country.supportsCrypto && (
                                    <Tooltip title="Supports cryptocurrency payouts" arrow>
                                        <span className="cursor-help flex items-center">
                                            <div className="w-3 h-3 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 text-[8px] font-black">
                                                $
                                            </div>
                                        </span>
                                    </Tooltip>
                                )}
                                {country.supportsDebit && (
                                    <Tooltip title="Supports instant debit card payouts" arrow>
                                        <span className="cursor-help flex items-center">
                                            <CreditCard size={12} className="text-light-primary dark:text-dark-primary" />
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default StripeOnboardingWithdrawModal